import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo } from 'react';
import { IdControlProvider } from '@shared-ui/components';
import { buildAdminCapabilities } from './idControl/adminCapabilities';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { You } from './pages/You';
import { Dashboard } from './pages/Dashboard';
import { useIsNonPublicTenant } from './hooks/useMyTenant';
import { Operations } from './pages/operations/Operations';
import { SolutionAnalysisDetail } from './pages/operations/SolutionAnalysisDetail';
import { HintDetail } from './pages/operations/HintDetail';
import { HintQualityQuestionRoute } from './pages/operations/HintQualityTab';
import { SolutionSplitterDetail } from './pages/operations/SolutionSplitterDetail';
import { QualityCheckDrilldown } from './pages/operations/QualityCheckDrilldown';
import { ConceptLabDetail } from './pages/operations/ConceptLabDetail';
import { ExtractionDetail } from './pages/operations/ExtractionDetail';
import { QuestionSessionReportDetail } from './pages/operations/QuestionSessionReportDetail';
import { QuestionSessionReportBySession } from './pages/operations/QuestionSessionReportBySession';
import { MarkAttemptDetail } from './pages/operations/MarkAttemptDetail';
import { CohortClusterDetail } from './pages/operations/CohortClusterDetail';
import { GenerationDetail } from './pages/operations/GenerationDetail';
import { Users } from './pages/users/Users';
import { UserDetail } from './pages/users/UserDetail';
import { Settings } from './pages/settings/Settings';
import { Monitoring } from './pages/monitoring/Monitoring';
import { QuestionFindings } from './pages/monitoring/questionQuality/QuestionFindings';
import { PersonaAgents } from './pages/persona-agents/PersonaAgents';
import { UserActions } from './pages/user-actions/UserActions';
import { StickingPatterns } from './pages/sticking-patterns/StickingPatterns';
import { Adjustments } from './pages/adjustments/Adjustments';
import { PersonaAgentRunDetail } from './pages/persona-agents/PersonaAgentRunDetail';
import { PersonaAgentPersonaDetail } from './pages/persona-agents/PersonaAgentPersonaDetail';
import { Questions } from './pages/questions/Questions';
import { AdminBookmarks } from './pages/bookmarks/AdminBookmarks';
import { Deletions } from './pages/questions/Deletions';
import { Tests } from './pages/tests/Tests';
import { TestPdfView } from './pages/tests/TestPdfView';
import { AssignmentsAndTests } from './pages/assignments-tests/AssignmentsAndTests';
import { PaperSittings } from './pages/assignments-tests/PaperSittings';
import { TestReportViewer } from './pages/assignments-tests/TestReportViewer';
import { ModulesCurriculum } from './pages/ModulesCurriculum';
import { Programs } from './pages/Programs';
import { CurriculumDetail } from './pages/CurriculumDetail';
import { Schools } from './pages/Schools';
import { SchoolClassDetail } from './pages/SchoolClassDetail';
import { ExamDetail } from './pages/ExamDetail';
import { BookDetail } from './pages/BookDetail';

import { ProblemTypeGraph } from './pages/ProblemTypeGraph';
import { Prompts } from './pages/prompts/Prompts';
import { ModelData } from './pages/prompts/ModelData';
import { ModelRunDetail } from './pages/prompts/ModelRunDetail';
import EvalDatasets from './pages/evals/EvalDatasets';
import EvalDatasetDetail from './pages/evals/EvalDatasetDetail';
import EvalRuns from './pages/evals/EvalRuns';
import Evals from './pages/evals/Evals';
import AssignmentQualityDetail from './pages/evals/assignments/AssignmentQualityDetail';
import AssignmentAnalyses from './pages/evals/assignments/AssignmentAnalyses';
import StudentAnalyses from './pages/evals/assignments/StudentAnalyses';
import LabelingInterface from './pages/evals/LabelingInterface';
import ScenarioDatasetDetail from './pages/scenario-evals/ScenarioDatasetDetail';
import Subscriptions from './pages/subscriptions/Subscriptions';
import Codes from './pages/subscriptions/Codes';
import Orders from './pages/subscriptions/Orders';
import PaperImports from './paperImport/PaperImports';
import AdminUsers from './pages/platform/admin-users/AdminUsers';
import Tenants from './pages/platform/tenants/Tenants';
import TenantDetail from './pages/platform/tenants/TenantDetail';
import TenantAdmins from './pages/admin/tenant-admins/TenantAdmins';
import { AdminFeedbackPage } from './pages/feedback/AdminFeedbackPage';
import { AdminHelpQueries } from './pages/threads/AdminHelpQueries';
import { LearningMomentsHost } from './pages/LearningMomentsHost';
import { LearningMomentsObserved } from './pages/LearningMomentsObserved';
import { InsightCategories } from './pages/learning-moments/InsightCategories';
import { InsightRuns } from './pages/learning-moments/InsightRuns';
import { InsightTaxonomy } from './pages/learning-moments/InsightTaxonomy';
import { InsightNodeDrill } from './pages/learning-moments/InsightNodeDrill';
import { WhatsNewTips } from './pages/WhatsNewTips';
import { GuidedExperimentPage } from './pages/simulations/GuidedExperimentPage';
import SiteAnalytics from './pages/site-usage/SiteUsagePage';
import PublishedLinks from './pages/PublishedLinks';
import MarketingContent from './pages/marketing/MarketingContent';
import MarketingInstagram from './pages/marketing/MarketingInstagram';
import MarketingYouTube from './pages/marketing/MarketingYouTube';
import PublishedLinkDetail from './pages/PublishedLinkDetail';
import { Simulations } from './pages/simulations/Simulations';
import { SimulationPlayer } from './pages/simulations/SimulationPlayer';
import { Scenes } from './pages/scenes/Scenes';
import { SceneQuestion } from './pages/scenes/SceneQuestion';
import { authApi } from './api/auth';
import { canAccessPath, canCurateChemistry, firstVisibleLeafPath, tabRequiresTenantForSuperAdmin, effectiveRoleNames } from './config/adminRoles';
import { getSelectedTenantId, hasScopedTenant } from './components/TenantFilter';
import { SessionExpiredDialog } from '@shared-ui/components/SessionExpiredDialog';
import './App.css';
import { currentTenantPrefix } from '@shared-ui/tenant';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds
      retry: 1,
    },
  },
});

/**
 * Route guard: only super-admin can access
 */
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  if (!authApi.isSuperAdmin()) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/** Old links to the Chemistry Lab page land on the Simulations tab that took
 *  its place. */
function ChemistryLabRedirect() {
  const [params] = useSearchParams();
  const tab = params.get('tab') === 'bench' ? 'quantitative-chemistry' : params.get('tab') === 'experiments' ? null : 'reactions';
  return <Navigate to={tab ? `/simulations?tab=${tab}` : '/simulations'} replace />;
}

function ChemistryCuratorGuard({ children }: { children: React.ReactNode }) {
  return canCurateChemistry(authApi.getUser()) ? <>{children}</> : <Navigate to="/simulations" replace />;
}

/**
 * Role-based route guard. `tab` is the canonical nav path the route belongs
 * to (detail routes pass their parent tab — e.g. /users/:id passes '/users').
 * If the current admin's roles don't grant access, redirect to the first tab
 * they can see. Super-admin bypasses to /admin-users; unauthenticated users
 * are handled upstream by ProtectedRoute.
 */
function RoleGuard({ tab, children }: { tab: string; children: React.ReactNode }) {
  const user = authApi.getUser();
  if (!user) return <>{children}</>;  // ProtectedRoute will redirect
  // Super-admin sees everything (Platform group + all admin pages, scoped via TenantFilter).
  // Exception: pages flagged as `requiresTenantForSuperAdmin` need a specific
  // tenant picked in TenantFilter — without one, send the super-admin back to
  // the platform landing page (Platform Admins).
  if (user.isSuperAdmin) {
    if (tabRequiresTenantForSuperAdmin(tab) && !hasScopedTenant(getSelectedTenantId())) {
      return <Navigate to="/admin-users" replace />;
    }
    return <>{children}</>;
  }
  // Route guard reads the same effective roles the nav does, so a URL cannot
  // reach a tab the sidebar hides.
  const roles = effectiveRoleNames(user);
  if (canAccessPath(tab, roles)) return <>{children}</>;
  const fallback = firstVisibleLeafPath(roles) ?? '/login';
  return <Navigate to={fallback} replace />;
}

/**
 * Index route element. Renders the Dashboard if the admin has access, else
 * redirects to their first visible tab. Super-admins go to /admin-users.
 */
/**
 * Redirect a retired list route onto its tab, KEEPING the query string.
 *
 * These lists hold their filters in the query string, so a bare `<Navigate>`
 * silently drops whatever the user had filtered to — which is what happened
 * when the detail pages' "back" links, still pointing at the old paths, came
 * through here and landed on an unfiltered list.
 */
function TabRedirect({ to, tab }: { to: string; tab: string }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', tab);
  return <Navigate to={`${to}?${params.toString()}`} replace />;
}

/**
 * Withhold a platform-only surface from a school admin, so a pasted URL cannot
 * reach what the tab strip hides — the same invariant `RoleGuard` keeps for
 * roles, applied to the tenant axis.
 */
function PlatformOnly({ children }: { children: React.ReactNode }) {
  const user = authApi.getUser();
  const isNonPublicTenant = useIsNonPublicTenant();
  if (!isNonPublicTenant) return <>{children}</>;
  const fallback = firstVisibleLeafPath(effectiveRoleNames(user)) ?? '/login';
  return <Navigate to={fallback} replace />;
}

function IndexRedirect() {
  const user = authApi.getUser();
  // A school admin has no Dashboard: every tab on it answers a question about
  // the PLATFORM. Landing them on their first real page beats an empty one.
  const isNonPublicTenant = useIsNonPublicTenant();
  if (user?.isSuperAdmin) {
    // With a real tenant selected, the super-admin acts like a tenant-admin
    // and sees the dashboard scoped to that tenant. Otherwise (ALL_TENANTS
    // or the system 'platform' tenant), fall back to the platform landing
    // page (Platform Admins).
    if (!hasScopedTenant(getSelectedTenantId())) return <Navigate to="/admin-users" replace />;
    return <Dashboard />;
  }
  const roles = effectiveRoleNames(user);
  const fallback = firstVisibleLeafPath(roles) ?? '/login';
  if (isNonPublicTenant) return <Navigate to={fallback} replace />;
  if (canAccessPath('/', roles)) return <Dashboard />;
  return <Navigate to={fallback} replace />;
}

/**
 * Mounts IdControlProvider inside the router so capabilities can call useNavigate.
 */
function IdControlGate({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const capabilities = useMemo(() => buildAdminCapabilities(navigate), [navigate]);
  return <IdControlProvider capabilities={capabilities}>{children}</IdControlProvider>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionExpiredDialog />
      <BrowserRouter basename={currentTenantPrefix().basename}>
        <IdControlGate>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Printable question paper — outside the sidebar layout so it
              reads and prints as a clean document. */}
          <Route path="/tests/:id/pdf" element={<ProtectedRoute><RoleGuard tab="/tests"><TestPdfView /></RoleGuard></ProtectedRoute>} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<IndexRedirect />} />
            {/* No RoleGuard: every admin has an account, whatever they administer. */}
            <Route path="you" element={<You />} />
            <Route path="submissions" element={<RoleGuard tab="/submissions"><Operations /></RoleGuard>} />
            <Route path="submissions/:id" element={<RoleGuard tab="/submissions"><SolutionAnalysisDetail /></RoleGuard>} />
            <Route path="hints/:id" element={<RoleGuard tab="/submissions"><HintDetail /></RoleGuard>} />
            <Route path="hint-quality/:questionId" element={<RoleGuard tab="/submissions"><HintQualityQuestionRoute /></RoleGuard>} />
            <Route path="solution-splits/:id" element={<RoleGuard tab="/submissions"><SolutionSplitterDetail /></RoleGuard>} />
            <Route path="pipeline-checks/:name" element={<RoleGuard tab="/submissions"><QualityCheckDrilldown /></RoleGuard>} />
            <Route path="conceptlab/:id" element={<RoleGuard tab="/submissions"><ConceptLabDetail /></RoleGuard>} />
            <Route path="generation/:id" element={<RoleGuard tab="/submissions"><GenerationDetail /></RoleGuard>} />
            <Route path="extraction/:id" element={<RoleGuard tab="/submissions"><ExtractionDetail /></RoleGuard>} />
            <Route path="questionsessionreport/by-session/:sessionId" element={<RoleGuard tab="/submissions"><QuestionSessionReportBySession /></RoleGuard>} />
            <Route path="questionsessionreport/:id" element={<RoleGuard tab="/submissions"><QuestionSessionReportDetail /></RoleGuard>} />
            <Route path="markattempt/:id" element={<RoleGuard tab="/submissions"><MarkAttemptDetail /></RoleGuard>} />
            <Route path="cohortcluster/:id" element={<RoleGuard tab="/submissions"><CohortClusterDetail /></RoleGuard>} />
            <Route path="users" element={<RoleGuard tab="/users"><Users /></RoleGuard>} />
            <Route path="users/:id" element={<RoleGuard tab="/users"><UserDetail /></RoleGuard>} />
            <Route path="questions" element={<RoleGuard tab="/questions"><Questions /></RoleGuard>} />
            <Route path="questions/deletions" element={<RoleGuard tab="/questions"><Deletions /></RoleGuard>} />
            <Route path="bookmarks" element={<RoleGuard tab="/bookmarks"><AdminBookmarks /></RoleGuard>} />
            <Route path="tests" element={<RoleGuard tab="/tests"><Tests /></RoleGuard>} />
            <Route path="assignments-tests" element={<RoleGuard tab="/assignments-tests"><AssignmentsAndTests /></RoleGuard>} />
            <Route path="assignments-tests/report/:reportId" element={<RoleGuard tab="/assignments-tests"><TestReportViewer /></RoleGuard>} />
            <Route path="assignments-tests/:questionSetId" element={<RoleGuard tab="/assignments-tests"><PaperSittings /></RoleGuard>} />
            {/* Modules, curricula, concepts and books share one page. The list
                routes redirect into their tab so existing links and any Back
                out of a detail page still land somewhere real. Detail routes
                stand alone and now guard on `/modules`: the merged nav entry
                carries the roles the individual entries used to, and
                `canAccessPath` defaults to allow for a path it cannot find. */}
            <Route path="modules" element={<RoleGuard tab="/modules"><ModulesCurriculum /></RoleGuard>} />
            <Route path="curriculum" element={<TabRedirect to="/modules" tab="curriculum" />} />
            <Route path="concepts" element={<TabRedirect to="/modules" tab="concepts" />} />
            <Route path="books" element={<TabRedirect to="/modules" tab="books" />} />
            <Route path="programs" element={<RoleGuard tab="/programs"><Programs /></RoleGuard>} />
            <Route path="curriculum/:id" element={<RoleGuard tab="/modules"><CurriculumDetail /></RoleGuard>} />
            <Route path="schools" element={<RoleGuard tab="/schools"><Schools /></RoleGuard>} />
            {/* Now a tab of the content library, beside Books — plans are bound to
                a curriculum, not to a class. The old path redirects so existing
                links and bookmarks still land on it. */}
            <Route path="study-plans" element={<TabRedirect to="/modules" tab="study-plans" />} />
            <Route path="schools/:schoolId/classes/:classId" element={<RoleGuard tab="/schools"><SchoolClassDetail /></RoleGuard>} />
            <Route path="schools/:schoolId/classes/:classId/exams/:examId" element={<RoleGuard tab="/schools"><ExamDetail /></RoleGuard>} />
            <Route path="books/:id" element={<RoleGuard tab="/modules"><PlatformOnly><BookDetail /></PlatformOnly></RoleGuard>} />
            {/* Problem Types is a tab of Learning Moments. The list redirects
                into that tab; the graph stays its own route and guards on
                `/learning-moments`, since `canAccessPath` defaults to ALLOW for
                a path no longer in the nav config. */}
            <Route path="problem-types" element={<TabRedirect to="/learning-moments" tab="problem-types" />} />
            <Route path="problem-types/:id" element={<RoleGuard tab="/learning-moments"><ProblemTypeGraph /></RoleGuard>} />
            <Route path="learning-moments" element={<RoleGuard tab="/learning-moments"><LearningMomentsHost /></RoleGuard>} />
            <Route path="learning-moments/categories" element={<RoleGuard tab="/learning-moments"><InsightCategories /></RoleGuard>} />
            <Route path="learning-moments/runs" element={<RoleGuard tab="/learning-moments"><InsightRuns /></RoleGuard>} />
            <Route path="learning-moments/taxonomy" element={<RoleGuard tab="/learning-moments"><InsightTaxonomy /></RoleGuard>} />
            <Route path="learning-moments/taxonomy/:runId/:kind/:nodeKey" element={<RoleGuard tab="/learning-moments"><InsightNodeDrill /></RoleGuard>} />
            <Route path="learning-moments/encounters" element={<RoleGuard tab="/learning-moments"><LearningMomentsObserved /></RoleGuard>} />
            <Route path="whats-new" element={<RoleGuard tab="/whats-new"><WhatsNewTips /></RoleGuard>} />
            <Route path="chemistry-lab" element={<ChemistryLabRedirect />} />
            <Route path="site-usage" element={<RoleGuard tab="/site-usage"><SiteAnalytics /></RoleGuard>} />
            <Route path="published-links" element={<RoleGuard tab="/published-links"><PublishedLinks /></RoleGuard>} />
            <Route path="marketing/content" element={<RoleGuard tab="/marketing/content"><MarketingContent /></RoleGuard>} />
            <Route path="marketing/instagram" element={<RoleGuard tab="/marketing/instagram"><MarketingInstagram /></RoleGuard>} />
            <Route path="marketing/youtube" element={<RoleGuard tab="/marketing/youtube"><MarketingYouTube /></RoleGuard>} />
            <Route path="published-links/:slug" element={<RoleGuard tab="/published-links"><PublishedLinkDetail /></RoleGuard>} />
            <Route path="simulations" element={<RoleGuard tab="/simulations"><Simulations /></RoleGuard>} />
            <Route path="simulations/experiments/:id" element={<RoleGuard tab="/simulations"><ChemistryCuratorGuard><GuidedExperimentPage /></ChemistryCuratorGuard></RoleGuard>} />
            <Route path="simulations/:id" element={<RoleGuard tab="/simulations"><SimulationPlayer /></RoleGuard>} />
            <Route path="scenes" element={<RoleGuard tab="/scenes"><Scenes /></RoleGuard>} />
            <Route path="scenes/:questionId" element={<RoleGuard tab="/scenes"><SceneQuestion /></RoleGuard>} />
            <Route path="prompts" element={<RoleGuard tab="/prompts"><Prompts /></RoleGuard>} />
            <Route path="model-data" element={<RoleGuard tab="/model-data"><ModelData /></RoleGuard>} />
            <Route path="model-data/:id" element={<RoleGuard tab="/model-data"><ModelRunDetail /></RoleGuard>} />
            <Route path="evals" element={<RoleGuard tab="/evals"><Evals /></RoleGuard>} />
            <Route path="eval/datasets" element={<RoleGuard tab="/evals"><EvalDatasets /></RoleGuard>} />
            <Route path="eval/datasets/:datasetId" element={<RoleGuard tab="/evals"><EvalDatasetDetail /></RoleGuard>} />
            <Route path="eval/datasets/:datasetId/label" element={<RoleGuard tab="/evals"><LabelingInterface /></RoleGuard>} />
            <Route path="eval/datasets/:datasetId/items/:itemId" element={<RoleGuard tab="/evals"><LabelingInterface /></RoleGuard>} />
            <Route path="eval/runs" element={<RoleGuard tab="/evals"><EvalRuns /></RoleGuard>} />
            <Route path="evals/assignments/:questionSetId" element={<RoleGuard tab="/evals"><AssignmentQualityDetail /></RoleGuard>} />
            <Route path="evals/assignments/:questionSetId/analyses" element={<RoleGuard tab="/evals"><AssignmentAnalyses /></RoleGuard>} />
            <Route path="evals/students/:studentId" element={<RoleGuard tab="/evals"><StudentAnalyses /></RoleGuard>} />
            {/* The hub is now a tab of Evals; the old path is kept as a redirect for bookmarks. */}
            <Route path="scenario-evals" element={<Navigate to="/evals?tab=scenarios" replace />} />
            <Route path="scenario-evals/:datasetId" element={<RoleGuard tab="/evals"><ScenarioDatasetDetail /></RoleGuard>} />
            <Route path="subscriptions" element={<SuperAdminRoute><Subscriptions /></SuperAdminRoute>} />
            <Route path="billing/codes" element={<SuperAdminRoute><Codes /></SuperAdminRoute>} />
            <Route path="billing/orders" element={<SuperAdminRoute><Orders /></SuperAdminRoute>} />
            <Route path="paper-imports" element={<RoleGuard tab="/paper-imports"><PaperImports /></RoleGuard>} />
            <Route path="user-feedback" element={<RoleGuard tab="/user-feedback"><AdminFeedbackPage /></RoleGuard>} />
            <Route path="help-queries" element={<RoleGuard tab="/help-queries"><AdminHelpQueries /></RoleGuard>} />
            <Route path="tenant-admins" element={<RoleGuard tab="/tenant-admins"><TenantAdmins /></RoleGuard>} />
            <Route path="settings" element={<RoleGuard tab="/settings"><Settings /></RoleGuard>} />
            <Route path="monitoring" element={<RoleGuard tab="/monitoring"><Monitoring /></RoleGuard>} />
            <Route path="question-quality/:questionId" element={<RoleGuard tab="/monitoring"><QuestionFindings /></RoleGuard>} />
            <Route path="persona-agents" element={<RoleGuard tab="/persona-agents"><PersonaAgents /></RoleGuard>} />
            <Route path="user-actions" element={<RoleGuard tab="/user-actions"><UserActions /></RoleGuard>} />
            <Route path="sticking-patterns" element={<RoleGuard tab="/sticking-patterns"><StickingPatterns /></RoleGuard>} />
            <Route path="adjustments" element={<RoleGuard tab="/adjustments"><Adjustments /></RoleGuard>} />
            <Route path="persona-agents/runs/:runId" element={<RoleGuard tab="/persona-agents"><PersonaAgentRunDetail /></RoleGuard>} />
            <Route path="persona-agents/personas/:personaId" element={<RoleGuard tab="/persona-agents"><PersonaAgentPersonaDetail /></RoleGuard>} />
            <Route
              path="admin-users"
              element={
                <SuperAdminRoute>
                  <AdminUsers />
                </SuperAdminRoute>
              }
            />
            <Route
              path="tenants"
              element={
                <SuperAdminRoute>
                  <Tenants />
                </SuperAdminRoute>
              }
            />
            <Route
              path="tenants/:id"
              element={
                <SuperAdminRoute>
                  <TenantDetail />
                </SuperAdminRoute>
              }
            />
          </Route>
        </Routes>
        </IdControlGate>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
